import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import { Trash3, Plus } from 'react-bootstrap-icons'
import { LocaleContext } from '../../../../contexts/LocaleContext'
import axiosService from '../../../../utils/AxiosService'
import { toastError } from '../../../FormComponents/Toasts'
import ChecklistItem from '../../../../models/ChecklistItem'

// Kept in sync with ChecklistItem::MAX_TEXT_LENGTH.
const MAX_ITEM_TEXT_LENGTH = 300

// Shared by the influencer's own submission (checkbox only - they can mark
// an item done, never add/remove one) and the PM's "submit on behalf" view
// (role="pm" additionally shows delete + "add requirement", since the PM is
// also the one who defines the checklist). Merging these into one numbered
// section replaces what used to be two separate checklist widgets stacked
// on top of each other pre-submission - a plain manage-only list with no
// section number, sitting between numbered sections, plus this one.
export default observer(function InfluencerChecklistSection({ actionDraft, workingVersion, apiBase = '/api/advertiser/draft_share', heading = 'Checklist', role = 'influencer' }) {
  const { id, action_id } = useParams()
  const { localeState } = useContext(LocaleContext)
  const [newItemText, setNewItemText] = useState('')
  const canManage = role === 'pm'

  const manageBase = `/api/project_manager/campaigns/${id}/actions/${action_id}/checklist_items`

  const toggleItem = (item) => {
    const checked = !workingVersion.isChecked(item.id)
    workingVersion.setChecked(item.id, checked)

    axiosService.post(`${apiBase}/draft_versions/${workingVersion.id}/checklist_item_checks`, {
      checklist_item_check: { checklist_item_id: item.id, checked },
    }).catch(({ data: { error } }) => {
      workingVersion.setChecked(item.id, !checked)
      toastError(error)
    })
  }

  const addItem = () => {
    if (!newItemText.trim()) return

    const textField = localeState.locale === 'de' ? 'text_de' : 'text_en'
    axiosService.post(manageBase, { checklist_item: { [textField]: newItemText.trim() } })
      .then(item => {
        actionDraft.checklist_items.push(new ChecklistItem(item))
        setNewItemText('')
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const removeItem = (item) => {
    axiosService.delete(`${manageBase}/${item.id}`)
      .then(() => {
        actionDraft.checklist_items = actionDraft.checklist_items.filter(i => i.id !== item.id)
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  return (
    <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
      <p className="font-bold mb-3 dark:text-white">{heading}</p>

      {actionDraft.checklist_items.map(item => {
        const checked = workingVersion.isChecked(item.id)
        const needsFix = item.reviewed_status === 'failed'

        return (
          <div
            key={item.id}
            className={`flex items-center justify-between gap-2 py-2 px-2 -mx-2 rounded-lg border-b-1 border-rb-widget-border dark:border-rb-dark-mode-lighter last:border-none ${needsFix ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
          >
            <label className="flex items-center gap-2 min-w-0">
              <input type="checkbox" checked={checked} onChange={() => toggleItem(item)} className="text-rb-green rounded-sm shrink-0" />
              {/* min-w-0 needed for break-words to actually apply inside a flex parent */}
              <span className={`text-sm dark:text-white break-words min-w-0 ${checked ? 'line-through text-rb-mat-grey dark:text-rb-dark-mode-text-grey' : ''}`}>
                {item.text(localeState.locale)}
              </span>
            </label>
            {canManage && (
              <button onClick={() => removeItem(item)} className="shrink-0">
                <Trash3 className="text-red-500" />
              </button>
            )}
          </div>
        )
      })}

      {canManage && (
        <div>
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              value={newItemText}
              onChange={e => setNewItemText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="Your checklist item"
              maxLength={MAX_ITEM_TEXT_LENGTH}
              className="flex-1 p-2 border-1 border-rb-lite-grey rounded-lg focus:ring-transparent focus:border-rb-green dark:bg-rb-dark-mode dark:text-white dark:border-rb-dark-mode-lighter"
            />
            <button
              onClick={addItem}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white rounded-lg bg-rb-green"
            >
              <Plus /> Add requirement
            </button>
          </div>
          <p className="text-xs text-rb-mat-grey dark:text-rb-dark-mode-text-grey mt-1 text-right">
            {newItemText.length}/{MAX_ITEM_TEXT_LENGTH}
          </p>
        </div>
      )}
    </div>
  )
})
